import { FormsCustomTemplatesFragment } from 'generated/resources/graphql'

import { type CardProperty } from './properties/card'
import { type ListFields } from './properties/list'
import { type TableColumn } from './properties/table'

type TableVisualization = {
  type: 'table'
  options: {
    columns: TableColumn[]
    frozenColumns: 1
  }
}

type ListVisualization = {
  type: 'list'
  options: {
    fields: ListFields['fields']
    itemDefinition: ListFields['itemDefinition']
  }
}

type CardVisualization = {
  type: 'card'
  options: {
    title: (item: FormsCustomTemplatesFragment) => string
    cardProperties: CardProperty[]
  }
}

type Visualization = TableVisualization | ListVisualization | CardVisualization

const buildTableVisualization = (tableColumns: TableColumn[]): TableVisualization => ({
  type: 'table' as const,
  options: {
    columns: tableColumns,
    frozenColumns: 1 as const,
  },
})

const buildListVisualization = (listFields: ListFields): ListVisualization => ({
  type: 'list' as const,
  options: {
    fields: listFields.fields,
    itemDefinition: listFields.itemDefinition,
  },
})

const buildCardVisualization = (cardProperties: CardProperty[]): CardVisualization => ({
  type: 'card' as const,
  options: {
    title: (item: FormsCustomTemplatesFragment) => item.title,
    cardProperties,
  },
})

type VisualizationMode = 'table' | 'list' | 'card' | 'table+card' | 'all'

type VisualizationInputs = {
  tableColumns: TableColumn[]
  listFields: ListFields
  cardProperties: CardProperty[]
}

export const buildVisualizations = (
  mode: VisualizationMode,
  { tableColumns, listFields, cardProperties }: VisualizationInputs
): Visualization[] => {
  switch (mode) {
    case 'list':
      return [buildListVisualization(listFields)]
    case 'card':
      return [buildCardVisualization(cardProperties)]
    case 'table':
      return [buildTableVisualization(tableColumns)]
    case 'table+card':
      return [buildCardVisualization(cardProperties), buildTableVisualization(tableColumns)]
    default:
    case 'all':
      return [buildTableVisualization(tableColumns), buildListVisualization(listFields)]
  }
}
